import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSearch, FaTimes, FaUser, FaShoppingCart, FaBox, FaCrown, FaEnvelope, FaSpinner } from 'react-icons/fa';
import GlobalSearchService from '../../../service/admin/globalSearchService';
import './globalSearch.css';

// Debounce hook
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export default function GlobalSearch() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const searchRef = useRef(null);
  const inputRef = useRef(null);

  const debouncedQuery = useDebounce(query, 300);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search when query changes
  useEffect(() => {
    if (debouncedQuery.length >= 2) {
      performSearch(debouncedQuery, activeFilter);
    } else {
      setResults(null);
    }
  }, [debouncedQuery, activeFilter]);

  const performSearch = async (searchQuery, type) => {
    setIsLoading(true);
    try {
      const res = await GlobalSearchService.search(searchQuery, type, 1, 5);
      if (res?.status?.code === 200) {
        setResults(res.data);
        setIsOpen(true);
      }
    } catch (err) {
      console.error('Search error:', err);
    }
    setIsLoading(false);
  };

  const handleInputChange = (e) => {
    setQuery(e.target.value);
    if (e.target.value.length >= 2) {
      setIsOpen(true);
    }
  };

  const handleInputFocus = () => {
    if (query.length >= 2 && results) {
      setIsOpen(true);
    }
  };

  const handleClear = () => {
    setQuery('');
    setResults(null);
    setIsOpen(false);
    inputRef.current?.focus();
  };

  const handleResultClick = (link) => {
    navigate(link);
    setIsOpen(false);
    setQuery('');
  };

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusColor = (status) => {
    const statusColors = {
      'payment pending': '#f59e0b',
      'processing': '#3b82f6',
      'shipped': '#8b5cf6',
      'delivered': '#10b981',
      'cancelled': '#ef4444',
      'refunded': '#6b7280',
    };
    return statusColors[status?.toLowerCase()] || '#6b7280';
  };

  const filters = [
    { key: 'all', label: 'All' },
    { key: 'users', label: 'Users' },
    { key: 'orders', label: 'Orders' },
    { key: 'products', label: 'Products' },
    { key: 'subscriptions', label: 'Plans' },
    { key: 'inquiries', label: 'Inquiries' },
  ];

  const hasResults = results && (
    results.users?.length > 0 ||
    results.orders?.length > 0 ||
    results.products?.length > 0 ||
    results.subscriptions?.length > 0 ||
    results.inquiries?.length > 0
  );

  return (
    <div className="gs-container" ref={searchRef}>
      <div className="gs-input-wrapper">
        <FaSearch className="gs-input-icon" />
        <input
          ref={inputRef}
          type="text"
          className="gs-input"
          placeholder="Search users, orders, products..."
          value={query}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
        />
        {isLoading && <FaSpinner className="gs-spinner" />}
        {query && !isLoading && (
          <button className="gs-clear-btn" onClick={handleClear}>
            <FaTimes />
          </button>
        )}
      </div>

      {isOpen && query.length >= 2 && (
        <div className="gs-dropdown">
          {/* Filters */}
          <div className="gs-filters">
            {filters.map((filter) => (
              <button
                key={filter.key}
                className={`gs-filter-btn ${activeFilter === filter.key ? 'gs-filter-active' : ''}`}
                onClick={() => handleFilterChange(filter.key)}
              >
                {filter.label}
                {results && filter.key !== 'all' && results[`${filter.key}Count`] > 0 && (
                  <span className="gs-filter-count">{results[`${filter.key}Count`]}</span>
                )}
              </button>
            ))}
          </div>

          {/* Results */}
          <div className="gs-results">
            {!hasResults && !isLoading && (
              <div className="gs-no-results">
                <p>No results found for "{query}"</p>
                <span>Try different keywords or filters</span>
              </div>
            )}

            {/* Users */}
            {(activeFilter === 'all' || activeFilter === 'users') && results?.users?.length > 0 && (
              <div className="gs-section">
                <div className="gs-section-header">
                  <FaUser className="gs-section-icon" />
                  <span>Users</span>
                  <span className="gs-section-count">{results.usersCount}</span>
                </div>
                {results.users.map((user) => (
                  <div
                    key={user._id}
                    className="gs-result-item"
                    onClick={() => handleResultClick(user.link)}
                  >
                    <div className="gs-result-avatar gs-avatar-user">
                      {user.name?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                    <div className="gs-result-content">
                      <div className="gs-result-title">{user.name || 'Unknown'}</div>
                      <div className="gs-result-subtitle">{user.email}</div>
                    </div>
                    <div className="gs-result-meta">
                      <span className={`gs-badge ${user.role === 'admin' ? 'gs-badge-admin' : 'gs-badge-seller'}`}>
                        {user.role}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Orders */}
            {(activeFilter === 'all' || activeFilter === 'orders') && results?.orders?.length > 0 && (
              <div className="gs-section">
                <div className="gs-section-header">
                  <FaShoppingCart className="gs-section-icon" />
                  <span>Orders</span>
                  <span className="gs-section-count">{results.ordersCount}</span>
                </div>
                {results.orders.map((order) => (
                  <div
                    key={order._id}
                    className="gs-result-item"
                    onClick={() => handleResultClick(order.link)}
                  >
                    <div className="gs-result-avatar gs-avatar-order">
                      <FaShoppingCart />
                    </div>
                    <div className="gs-result-content">
                      <div className="gs-result-title">{order.orderId}</div>
                      <div className="gs-result-subtitle">{order.customerName} • {formatDate(order.createdAt)}</div>
                    </div>
                    <div className="gs-result-meta">
                      <span className="gs-result-amount">{formatCurrency(order.totalAmount)}</span>
                      <span className="gs-status-dot" style={{ background: getStatusColor(order.status) }}></span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Products */}
            {(activeFilter === 'all' || activeFilter === 'products') && results?.products?.length > 0 && (
              <div className="gs-section">
                <div className="gs-section-header">
                  <FaBox className="gs-section-icon" />
                  <span>Products</span>
                  <span className="gs-section-count">{results.productsCount}</span>
                </div>
                {results.products.map((product) => (
                  <div
                    key={product._id}
                    className="gs-result-item"
                    onClick={() => handleResultClick(product.link)}
                  >
                    {product.image ? (
                      <img src={product.image} alt="" className="gs-result-img" />
                    ) : (
                      <div className="gs-result-avatar gs-avatar-product">
                        <FaBox />
                      </div>
                    )}
                    <div className="gs-result-content">
                      <div className="gs-result-title gs-truncate">{product.name}</div>
                      <div className="gs-result-subtitle">SKU: {product.sku}</div>
                    </div>
                    <div className="gs-result-meta">
                      <span className="gs-result-amount">{formatCurrency(product.price)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Subscriptions */}
            {(activeFilter === 'all' || activeFilter === 'subscriptions') && results?.subscriptions?.length > 0 && (
              <div className="gs-section">
                <div className="gs-section-header">
                  <FaCrown className="gs-section-icon" />
                  <span>Subscription Plans</span>
                  <span className="gs-section-count">{results.subscriptionsCount}</span>
                </div>
                {results.subscriptions.map((sub) => (
                  <div
                    key={sub._id}
                    className="gs-result-item"
                    onClick={() => handleResultClick(sub.link)}
                  >
                    <div className="gs-result-avatar gs-avatar-sub">
                      <FaCrown />
                    </div>
                    <div className="gs-result-content">
                      <div className="gs-result-title">{sub.name}</div>
                      <div className="gs-result-subtitle">{sub.duration} days</div>
                    </div>
                    <div className="gs-result-meta">
                      <span className="gs-result-amount">{formatCurrency(sub.price)}</span>
                      <span className={`gs-badge ${sub.isActive ? 'gs-badge-active' : 'gs-badge-inactive'}`}>
                        {sub.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Inquiries */}
            {(activeFilter === 'all' || activeFilter === 'inquiries') && results?.inquiries?.length > 0 && (
              <div className="gs-section">
                <div className="gs-section-header">
                  <FaEnvelope className="gs-section-icon" />
                  <span>Inquiries</span>
                  <span className="gs-section-count">{results.inquiriesCount}</span>
                </div>
                {results.inquiries.map((inquiry) => (
                  <div
                    key={inquiry._id}
                    className="gs-result-item"
                    onClick={() => handleResultClick(inquiry.link)}
                  >
                    <div className="gs-result-avatar gs-avatar-inquiry">
                      <FaEnvelope />
                    </div>
                    <div className="gs-result-content">
                      <div className="gs-result-title">{inquiry.name}</div>
                      <div className="gs-result-subtitle">{inquiry.subject || inquiry.email}</div>
                    </div>
                    <div className="gs-result-meta">
                      <span className={`gs-badge gs-badge-${inquiry.status?.toLowerCase() || 'pending'}`}>
                        {inquiry.status || 'Pending'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {hasResults && (
            <div className="gs-footer">
              <span className="gs-total">
                {results.totalResults} result{results.totalResults !== 1 ? 's' : ''} found
              </span>
              <span className="gs-hint">Press Enter to see all results</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
